import { ui } from '@procuros/datachecks';
import { ColumnDef } from '@tanstack/react-table';

import { WebEDIRowData } from 'support/types/webediTable';

import { ExcelColumnDefinition } from '../types';

/**
 * Converts DataTable column definitions to Excel column definitions
 * Handles both regular columns and grouped columns (with nested sub-columns)
 * @param columns Array of DataTable column definitions
 * @returns Array of Excel column definitions
 */
export const dataTableColumnsToExcelDefinitions = (
  columns: Array<ColumnDef<WebEDIRowData, unknown>>,
): Array<ExcelColumnDefinition> => {
  return columns
    .map((column) => convertSingleColumn(column))
    .filter((column): column is ExcelColumnDefinition => column !== null);
};

const isValidFieldType = (type: unknown): type is ui.FieldTypes =>
  Object.values(ui.FieldTypes).includes(type as ui.FieldTypes);

const convertSingleColumn = (column: ColumnDef<WebEDIRowData, unknown>): ExcelColumnDefinition | null => {
  const requirement = column.meta?.requirement ?? 'optional';
  const label = column.meta?.label;
  const type = column.meta?.type;

  if (!label || !column.id || !isValidFieldType(type)) {
    return null;
  }

  const header = requirement === 'required' ? `${label} *` : label;

  const baseDefinition: ExcelColumnDefinition = {
    key: column.id,
    header: header,
    description: column.meta?.description,
    type,
    example: undefined,
    requirement,
    options: column.meta?.optionalConfig?.options,
  };

  if ('columns' in column && Array.isArray(column.columns) && column.columns.length > 0 && column.id) {
    const subColumns = column.columns
      .map((subColumn) => {
        return convertSingleColumn(subColumn);
      })
      .filter((col): col is Exclude<ExcelColumnDefinition, 'columns'> => col !== null);

    if (subColumns.length > 0) {
      baseDefinition.columns = subColumns;
    }
  }

  return baseDefinition;
};
